// SectionCard — the most-used screen building block: a Surface with a standard
// header (eyebrow + title on the left, an action slot on the right — e.g. a
// PeriodFilter, AsOfPill, or link) over a body. Handles its own loading state
// so screens don't re-implement it. Reusability 10/10.
import PropTypes from 'prop-types';
import { cx } from '../../../utils/cx';
import { Surface } from './Surface';
import { Eyebrow } from '../data-display/Eyebrow';
import { AsOfPill } from '../data-display/AsOfPill';
import { Skeleton } from '../feedback/Skeleton';
import './section-card.css';

export function SectionCard({
  title,
  eyebrow,
  eyebrowTone,
  action,
  asOf = false,
  loading = false,
  variant,
  padding = 'md',
  headerless = false,
  className,
  children,
  ...rest
}) {
  return (
    <Surface
      as="section"
      variant={variant}
      padding={padding}
      className={cx('section-card', className)}
      {...rest}
    >
      {!headerless && (title || eyebrow || action || asOf) && (
        <header className="section-card__head">
          <div className="section-card__head-l">
            {eyebrow && <Eyebrow tone={eyebrowTone}>{eyebrow}</Eyebrow>}
            {title && <h2 className="section-card__title">{title}</h2>}
          </div>
          <div className="section-card__head-r">
            {asOf && <AsOfPill date={asOf === true ? undefined : asOf} />}
            {action}
          </div>
        </header>
      )}
      <div className="section-card__body">
        {loading ? (
          <div className="section-card__loading">
            <Skeleton variant="text" count={3} />
          </div>
        ) : (
          children
        )}
      </div>
    </Surface>
  );
}

SectionCard.propTypes = {
  title: PropTypes.node,
  eyebrow: PropTypes.node,
  eyebrowTone: PropTypes.string,
  /** Right-aligned header slot (PeriodFilter, link, button, etc.). */
  action: PropTypes.node,
  /** true → show an AsOfPill (yesterday); or pass a Date/string. */
  asOf: PropTypes.oneOfType([PropTypes.bool, PropTypes.instanceOf(Date), PropTypes.string]),
  loading: PropTypes.bool,
  variant: PropTypes.oneOf(['elevated', 'inset', 'outlined', 'flat']),
  padding: PropTypes.oneOf(['none', 'sm', 'md', 'lg']),
  /** Skip the header entirely (body-only card). */
  headerless: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default SectionCard;
